"use client";

import * as React from "react";
import { useShellTier, type ShellTier } from "../../hooks/use-media-query";
import { LayoutSettleProvider } from "../../hooks/use-layout-settle";
import { SidebarContext } from "../../components/ui/sidebar";
import type { Omittable } from "../../lib/props";

/**
 * Shell state shared with descendants. Each tier presents the pane its own
 * way, and each presentation has its own state: `open` is the FULL tier's
 * pinned pane expanded-vs-collapsed (a persisted layout preference),
 * `openOverlay` is the MEDIUM tier's flyout (ephemeral), and `openMobile` is
 * the COMPACT tier's drawer (ephemeral). Mirrors the fields a consumer
 * reaches for through {@link useAppShell}.
 */
export type AppShellContextValue = {
  /** The shell's width tier — see {@link useShellTier}. */
  tier: ShellTier;
  /** `tier === "compact"` — kept as the name most call sites reach for. */
  isMobile: boolean;
  /** Full tier only: the module-menu pane is expanded vs collapsed to the rail. */
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Compact tier only: the nav drawer is presented vs dismissed. */
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  /** Medium tier only: the pane is presented vs auto-collapsed. Ephemeral —
   *  the medium pane starts collapsed on every load — but sticky while it
   *  lives: navigation does not collapse it, only the user (or an action
   *  like entering settings) moves it. */
  openOverlay: boolean;
  setOpenOverlay: (open: boolean) => void;
  /** Derived from `open` — `data-state` styling-hook parity with the base parts. */
  state: "expanded" | "collapsed";
  /** Toggles the tier's own pane presentation: the compact drawer, the medium
   *  flyout, or the full pinned pane. */
  toggleSidebar: () => void;
  /** Current pane width in px — the pane alone, NOT including the rail. */
  sidebarWidth: number;
  /** Streams a dragged pane width. Clamped by the caller of the seam. */
  setSidebarWidth: (width: number) => void;
  /** Clamp bounds a resize seam must honour. */
  minSidebarWidth: number;
  maxSidebarWidth: number;
  /**
   * True while a seam is dragging. The shell drops its width transition for
   * the duration — otherwise the pane eases toward each streamed width and
   * trails the pointer by the animation duration.
   */
  isResizingSidebar: boolean;
  setResizingSidebar: (resizing: boolean) => void;
  /** Current right-panel island width in px (desktop). */
  rightPanelWidth: number;
  /** Streams a dragged right-panel width. Clamped by the caller of the seam. */
  setRightPanelWidth: (width: number) => void;
  /** Clamp bounds a right-panel resize seam must honour. */
  minRightPanelWidth: number;
  maxRightPanelWidth: number;
  /** True while the right-panel seam is dragging — same contract as
   *  {@link AppShellContextValue.isResizingSidebar}, for the island column. */
  isResizingRightPanel: boolean;
  setResizingRightPanel: (resizing: boolean) => void;
};

const AppShellContext = React.createContext<AppShellContextValue | null>(null);

export function useAppShell(): AppShellContextValue {
  const context = React.useContext(AppShellContext);
  if (!context) {
    throw new Error("useAppShell must be used within an AppShellProvider.");
  }
  return context;
}

export type AppShellProviderProps = Omittable<{
  /** Initial desktop pane state for uncontrolled mode. Defaults to `true`. */
  defaultOpen?: boolean;
  /** Controlled desktop pane state — pass alongside `onOpenChange`. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Initial pane width for uncontrolled mode. Defaults to 250. */
  defaultSidebarWidth?: number;
  /** Controlled pane width — pass alongside `onSidebarWidthChange`. */
  sidebarWidth?: number;
  onSidebarWidthChange?: (width: number) => void;
  /** Clamp bounds published to any resize seam. Default 120 / 400. */
  minSidebarWidth?: number;
  maxSidebarWidth?: number;
  /** Initial right-panel width for uncontrolled mode. Defaults to 360. */
  defaultRightPanelWidth?: number;
  /** Controlled right-panel width — pass alongside `onRightPanelWidthChange`. */
  rightPanelWidth?: number;
  onRightPanelWidthChange?: (width: number) => void;
  /** Clamp bounds published to a right-panel resize seam. Default 360 / 720. */
  minRightPanelWidth?: number;
  maxRightPanelWidth?: number;
  children: React.ReactNode;
}>;

/**
 * Owns the shell's open state and exposes it via {@link useAppShell}. Full-tier
 * `open` is controllable (the layout persists it); `openMobile` and
 * `openOverlay` are always internal and ephemeral. No cookie, no keyboard
 * shortcut — the app drives persistence and the module-menu header owns the
 * collapse affordance.
 */
export function AppShellProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange,
  defaultSidebarWidth = 250,
  sidebarWidth: widthProp,
  onSidebarWidthChange,
  minSidebarWidth = 120,
  maxSidebarWidth = 400,
  defaultRightPanelWidth = 360,
  rightPanelWidth: rightPanelWidthProp,
  onRightPanelWidthChange,
  minRightPanelWidth = 360,
  maxRightPanelWidth = 720,
  children,
}: AppShellProviderProps): React.ReactElement {
  const tier = useShellTier();
  const isMobile = tier === "compact";
  const [openMobile, setOpenMobile] = React.useState(false);
  const [openOverlay, setOpenOverlay] = React.useState(false);

  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const open = openProp ?? uncontrolledOpen;

  const [uncontrolledWidth, setUncontrolledWidth] =
    React.useState(defaultSidebarWidth);
  const sidebarWidth = widthProp ?? uncontrolledWidth;
  const [isResizingSidebar, setResizingSidebar] = React.useState(false);

  const [uncontrolledRightPanelWidth, setUncontrolledRightPanelWidth] =
    React.useState(defaultRightPanelWidth);
  const rightPanelWidth = rightPanelWidthProp ?? uncontrolledRightPanelWidth;
  const [isResizingRightPanel, setResizingRightPanel] = React.useState(false);

  const setSidebarWidth = React.useCallback(
    (value: number) => {
      if (onSidebarWidthChange) {
        onSidebarWidthChange(value);
      } else {
        setUncontrolledWidth(value);
      }
    },
    [onSidebarWidthChange],
  );

  const setRightPanelWidth = React.useCallback(
    (value: number) => {
      if (onRightPanelWidthChange) {
        onRightPanelWidthChange(value);
      } else {
        setUncontrolledRightPanelWidth(value);
      }
    },
    [onRightPanelWidthChange],
  );

  const setOpen = React.useCallback(
    (value: boolean) => {
      if (onOpenChange) {
        onOpenChange(value);
      } else {
        setUncontrolledOpen(value);
      }
    },
    [onOpenChange],
  );

  const toggleSidebar = React.useCallback(() => {
    if (tier === "compact") {
      setOpenMobile((prev) => !prev);
    } else if (tier === "medium") {
      setOpenOverlay((prev) => !prev);
    } else {
      setOpen(!open);
    }
  }, [tier, open, setOpen]);

  const value = React.useMemo<AppShellContextValue>(
    () => ({
      tier,
      isMobile,
      open,
      openMobile,
      openOverlay,
      setOpen,
      setOpenMobile,
      setOpenOverlay,
      state: open ? "expanded" : "collapsed",
      toggleSidebar,
      sidebarWidth,
      setSidebarWidth,
      minSidebarWidth,
      maxSidebarWidth,
      isResizingSidebar,
      setResizingSidebar,
      rightPanelWidth,
      setRightPanelWidth,
      minRightPanelWidth,
      maxRightPanelWidth,
      isResizingRightPanel,
      setResizingRightPanel,
    }),
    [
      tier,
      isMobile,
      open,
      openMobile,
      openOverlay,
      setOpen,
      toggleSidebar,
      sidebarWidth,
      setSidebarWidth,
      minSidebarWidth,
      maxSidebarWidth,
      isResizingSidebar,
      rightPanelWidth,
      setRightPanelWidth,
      minRightPanelWidth,
      maxRightPanelWidth,
      isResizingRightPanel,
    ],
  );

  // The nav rows inside the module-menu pane are the base `SidebarMenu*`
  // primitives, and `SidebarMenuButton` calls `useSidebar()` unconditionally.
  // We drive our own shell rather than the base `Sidebar`, so publish the base
  // `SidebarContext` from here too — its shape is identical — so those rows
  // resolve their context instead of throwing.
  return (
    <AppShellContext.Provider value={value}>
      <SidebarContext.Provider value={value}>
        {/* The shell is the layout animator (the pane's width transition), so
            it hosts the settle window its measurement consumers subscribe
            to — see `use-layout-settle.tsx`. */}
        <LayoutSettleProvider>{children}</LayoutSettleProvider>
      </SidebarContext.Provider>
    </AppShellContext.Provider>
  );
}
